import React from "react";
import { cn } from "visuais";
import {
  IconHomeFilled,
  IconTrolleyFilled,
  IconZoomMoneyFilled,
  IconCalendarWeekFilled,
  IconShoppingCartFilled,
  IconUserFilled,
} from "@tabler/icons-react";
import type { Pedido } from "@apiInclui/pedido";
import { fmt } from "visuais";
import { Building2 } from "lucide-react";

// Tipos para os itens de informação
export interface InfoItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  align?: "start" | "center" | "end";
  truncate?: boolean;
}

// Função para gerar configurações da linha superior
export const getTopRowItems = (opt: Pedido): InfoItem[] => [
  {
    icon: IconHomeFilled,
    label: "Filial",
    value: opt.filPedido,
    align: "start" as const,
  },
  {
    icon: IconTrolleyFilled,
    label: "Entrega",
    value: opt.filEntrega,
    align: "center" as const,
  },
  {
    icon: IconZoomMoneyFilled,
    label: "Condição",
    value: opt.condicao || "-",
    align: "end" as const,
  },
];

// Função para gerar configurações da linha do meio
export const getMiddleRowItems = (opt: Pedido): InfoItem[] => [
  {
    icon: IconCalendarWeekFilled,
    label: "Emissão",
    value: fmt.dates.BR(opt.dataEmissao) || "-",
    align: "start" as const,
  },
  {
    icon: IconShoppingCartFilled,
    label: "Produtos",
    value: opt.produtos?.length || 0,    
    align: "center" as const,
  },
  {
    icon: IconUserFilled,
    label: "Solicitante",
    value: fmt.strings.capitalize(opt.solicitante) || "-",
    truncate: true,
    align: "end" as const,
  },
];

//função para gerar configurações da linha inferior
export const getBottomRowItems = (opt: Pedido): InfoItem[] => [
  {
    icon: Building2,
    label: "CNPJ",
    value: fmt.strings.cnpj(opt.cgcFornecedor),
    align: "center" as const
  },
];

// Função para renderizar itens de informação
export const renderInfoItem = (item: InfoItem, index: number) => (
  <div key={index} className="flex flex-col gap-0 flex-1">
    <div
      className={cn(
        "flex gap-2 items-center",
        item.align === "start" && "justify-start",
        item.align === "center" && "justify-center",
        item.align === "end" && "justify-end"
      )}
    >
      <item.icon className="text-cyan-600 dark:text-cyan-400" />
      <div className="flex flex-col gap-0">
        <span
          className={cn(
            "text-[9px] text-cyan-600 dark:text-cyan-400",
            item.align === "start" && "text-left",
            item.align === "center" && "text-center",
            item.align === "end" && "text-right"
          )}
        >
          {item.label}
        </span>
        <span
          className={cn(
            "font-medium text-[10pt] text-slate-900 dark:text-slate-100",
            item.truncate && "truncate",
            item.align === "start" && "text-left",
            item.align === "center" && "text-center",
            item.align === "end" && "text-right"
          )}
        >
          {item.value}
        </span>
      </div>
    </div>
  </div>
);

// Função para determinar o estilo do status
export const getStatusStyle = (status?: string) => {
  if (!status) return "default";

  const statusLower = status.toLowerCase();

  if (statusLower.includes("liberado") || statusLower.includes("aprovado")) {
    return "success";
  }

  if (statusLower.includes("cancelado") || statusLower.includes("rejeitado")) {
    return "error";
  }

  if (statusLower.includes("pendente") || statusLower.includes("aguardando")) {
    return "pending";
  }

  return "default";
};

// Função para obter classes CSS do status
export const getStatusClasses = (status?: string) => {
  const statusType = getStatusStyle(status);

  const baseClasses = "text-[9px] px-1.5 py-0.5 font-medium";

  switch (statusType) {
    case "success":
      return cn(
        baseClasses,
        "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-600"
      );
    case "error":
      return cn(
        baseClasses,
        "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600"
      );
    case "pending":
      return cn(
        baseClasses,
        "bg-slate-100 dark:bg-slate-800/30 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600"
      );
    default:
      return cn(
        baseClasses,
        "bg-slate-100 dark:bg-slate-800/30 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600"
      );
  }
};

// Função principal para obter todas as configurações
export const getContentConfig = (opt: Pedido) => ({
  topRowItems: getTopRowItems(opt),
  middleRowItems: getMiddleRowItems(opt),
  bottomRowItems: getBottomRowItems(opt),
});
